import React, { useState, useEffect } from 'react';
import { doc, onSnapshot, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { ConnectionRoadmap } from './ConnectionRoadmap';
import { ForceExitButton } from './ForceExitButton';
import { ChatStepInterface } from './connection-steps/ChatStepInterface';
import { CallStepInterface } from './connection-steps/CallStepInterface';
import { VideoStepInterface } from './connection-steps/VideoStepInterface';
import { PhysicalMeetingInterface } from './connection-steps/PhysicalMeetingInterface';

interface ConnectionFlow {
  id: string;
  currentStep: 'chat' | 'call' | 'video' | 'physical';
  completedSteps: string[];
  status: 'active' | 'completed' | 'terminated';
  stepStartTime: any;
  stepDuration: number;
  participants: string[];
}

interface ConnectionFlowContainerProps {
  connectionId: string;
  currentUserId: string;
  onClose: () => void;
}

export const ConnectionFlowContainer: React.FC<ConnectionFlowContainerProps> = ({
  connectionId,
  currentUserId,
  onClose
}) => {
  const [connectionFlow, setConnectionFlow] = useState<ConnectionFlow | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onSnapshot(
      doc(db, 'connectionFlows', connectionId),
      (doc) => {
        if (doc.exists()) {
          const data = doc.data() as ConnectionFlow;
          setConnectionFlow(data);
          setLoading(false);
        } else {
          console.error('Connection flow not found');
          setLoading(false);
        }
      },
      (error) => {
        console.error('Error listening to connection flow:', error);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [connectionId]);

  // Timer logic
  useEffect(() => {
    if (!connectionFlow?.stepStartTime || !connectionFlow?.stepDuration || connectionFlow.stepDuration === 0) {
      return;
    }
    
    const interval = setInterval(() => {
      const elapsed = Date.now() - connectionFlow.stepStartTime.toMillis();
      const remaining = (connectionFlow.stepDuration * 60 * 1000) - elapsed;
      
      if (remaining <= 0) {
        handleStepTimeout();
      } else {
        setTimeRemaining(Math.ceil(remaining / 1000));
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [connectionFlow]);

  const handleStepTimeout = async () => {
    if (!connectionFlow) return;
    
    const newCompletedSteps = [...(connectionFlow.completedSteps || []), connectionFlow.currentStep];
    const nextStep = getNextStep(connectionFlow.currentStep);
    
    if (nextStep) {
      await updateDoc(doc(db, 'connectionFlows', connectionId), {
        currentStep: nextStep,
        stepStartTime: new Date(),
        stepDuration: getStepDuration(nextStep),
        completedSteps: newCompletedSteps
      });
    } else {
      // Final step reached
      await updateDoc(doc(db, 'connectionFlows', connectionId), {
        status: 'completed',
        completedAt: new Date(),
        completedSteps: newCompletedSteps
      });
    }
  };

  const handleForceExit = async (step: string, reason: string) => {
    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      status: 'terminated',
      terminationReason: reason,
      terminatedAt: new Date(),
      terminatedBy: currentUserId,
      terminatedStep: step
    });

    onClose();
  };

  const fastNav = async (target: 'chat' | 'call' | 'video' | 'physical') => {
    await updateDoc(doc(db, 'connectionFlows', connectionId), {
      currentStep: target,
      stepStartTime: new Date(),
      stepDuration: getStepDuration(target),
      status: 'active'
    });
  };

  const getNextStep = (currentStep: string): string | null => {
    const steps = ['chat', 'call', 'video', 'physical'];
    const currentIndex = steps.indexOf(currentStep);
    return currentIndex < steps.length - 1 ? steps[currentIndex + 1] : null;
  };

  const getStepDuration = (step: string): number => {
    const durations = { chat: 10, call: 5, video: 5, physical: 0 };
    return durations[step as keyof typeof durations] || 10;
  };

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p>Connection flow laden...</p>
        </div>
      </div>
    );
  }

  if (!connectionFlow) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center">
          <p className="text-red-600">Connection flow niet gevonden</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-gray-500 text-white rounded">
            Sluiten
          </button>
        </div>
      </div>
    );
  }

  if (connectionFlow.status === 'completed') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">✅</span>
          </div>
          <h3 className="text-xl font-semibold mb-2">Connectie Voltooid!</h3>
          <p className="text-gray-600 mb-4">
            Jullie hebben alle stappen succesvol doorlopen. Veel succes met jullie samenwerking!
          </p>
          <button onClick={onClose} className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
            Sluiten
          </button>
        </div>
      </div>
    );
  }

  if (connectionFlow.status === 'terminated') {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8 text-center max-w-md">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">❌</span>
          </div>
          <h3 className="text-xl font-semibold mb-2">Connectie Beëindigd</h3>
          <p className="text-gray-600 mb-4">
            Deze connectie flow is voortijdig beëindigd.
          </p>
          <button onClick={onClose} className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600">
            Sluiten
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-50 rounded-lg max-w-6xl w-full mx-4 h-5/6 overflow-hidden flex">
        {/* Left Panel - Roadmap */}
        <div className="w-1/3 bg-white p-4 border-r flex flex-col">
          <ConnectionRoadmap
            currentStep={connectionFlow.currentStep}
            completedSteps={connectionFlow.completedSteps || []}
            connectionId={connectionId}
            onForceExit={handleForceExit}
          />
          
          {/* Timer Display */}
          {connectionFlow.stepDuration > 0 && timeRemaining > 0 && (
            <div className="mt-6 p-4 bg-blue-50 rounded-lg">
              <div className="text-center">
                <div className="text-2xl font-mono text-blue-600 mb-2">
                  {formatTime(timeRemaining)}
                </div>
                <div className="text-sm text-gray-600">Tijd over</div>
                <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                  <div 
                    className="bg-blue-500 h-2 rounded-full transition-all"
                    style={{ 
                      width: `${100 - (timeRemaining / (connectionFlow.stepDuration * 60)) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Force Exit Button */}
          <div className="mt-4">
            <ForceExitButton 
              stepId={connectionFlow.currentStep}
              stepName={getStepName(connectionFlow.currentStep)}
              onForceExit={handleForceExit}
            />
          </div>

          {/* Dev Fast Navigation */}
          <div className="mt-4 p-3 bg-gray-50 rounded border">
            <div className="text-xs text-gray-500 mb-2">Snel naar stap (dev)</div>
            <div className="grid grid-cols-2 gap-2">
              <button className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300" onClick={() => fastNav('chat')}>Chat</button>
              <button className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300" onClick={() => fastNav('call')}>Bellen</button>
              <button className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300" onClick={() => fastNav('video')}>Video</button>
              <button className="px-2 py-1 text-sm bg-gray-200 rounded hover:bg-gray-300" onClick={() => fastNav('physical')}>Afspraak</button>
            </div>
          </div>
        </div>

        {/* Right Panel - Active Step Content */}
        <div className="flex-1 p-4 flex flex-col">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">
              Connectie Flow - {getStepName(connectionFlow.currentStep)}
            </h2>
            <button 
              onClick={onClose} 
              className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
            >
              ×
            </button>
          </div>

          <div className="flex-1 overflow-hidden">
            {connectionFlow.currentStep === 'chat' && (
              <ChatStepInterface 
                connectionId={connectionId} 
                currentUserId={currentUserId}
              />
            )}
            {connectionFlow.currentStep === 'call' && (
              <CallStepInterface 
                connectionId={connectionId} 
                currentUserId={currentUserId}
              />
            )}
            {connectionFlow.currentStep === 'video' && (
              <VideoStepInterface 
                connectionId={connectionId} 
                currentUserId={currentUserId}
              />
            )}
            {connectionFlow.currentStep === 'physical' && (
              <PhysicalMeetingInterface 
                connectionId={connectionId} 
                currentUserId={currentUserId}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper function
function getStepName(step: string): string {
  const names = {
    chat: 'Chat & Afspraak',
    call: 'Telefoongesprek', 
    video: 'Videogesprek',
    physical: 'Fysieke Ontmoeting'
  };
  return names[step as keyof typeof names] || step;
}


